import { type QueryKey } from '@tanstack/react-query';
import { type ReactElement, useCallback, useMemo, useState } from 'react';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { CoverageMetricTableCell } from '@/features/codeCoverage/pages/CoverageMetricTableCell';
import {
  resolveCoverageMetricThresholdColor,
  type CoverageMetricThresholdKey,
} from '@/features/codeCoverage/pages/coverageMetricThresholdColors';
import { useProjectMetricThresholds } from '@/features/projects/hooks/useProjectMetricThresholds';
import { DetailEditEntityDialog } from '@/features/shared/components/entityDetails/DetailEditEntityDialog';
import { TableBreakdownLine } from '@/features/shared/components/tableSystem/TableBreakdownLine';
import {
  TableActionsCell,
  TableCellContainer,
} from '@/features/shared/components/tableSystem/TableCells';
import {
  TABLE_GRID_HEADER_HEIGHT,
  TABLE_ROW_HEIGHT,
} from '@/features/shared/components/tableSystem/tableConstants';
import {
  STATUS_META_TIMESTAMP_CONTENT_SX,
  STATUS_META_TIMESTAMP_VALUE_WRAPPER_SX,
  TableBreakdownStack,
} from '@/features/shared/components/tableSystem/TableMetaCells';
import { tableSx } from '@/features/shared/components/tableSystem/tableSx';
import { formatEntityTagsLabel } from '@/features/shared/tags';
import { getEntityActionLabel } from '@/shared/copy/actions';
import { getDetailLabel } from '@/shared/copy/details';
import { getDialogMessage } from '@/shared/copy/dialogs';
import { getEmptyStateMessage } from '@/shared/copy/emptyStates';
import { ServerDataTable } from '@/ui/composites/ServerDataTable';
import { TableNameCell } from '@/ui/composites/table';
import { formatDate } from '@/ui/formatters/formatDate';
import { formatRelativeTime } from '@/ui/formatters/formatRelativeTime';
import {
  type GridColDef,
  type GridColumnVisibilityModel,
  type GridSortModel,
} from '@/ui/primitives';
import {
  DescriptionOutlinedIcon,
  EventOutlinedIcon,
  FolderOutlinedIcon,
} from '@/ui/primitives/icons';
import { useCodeFileTableRowActions } from './useCodeFileTableRowActions';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { CodeFileDto, MetricThreshold, QueryResult } from '@flowqa/shared';

type CoverageCodeFilesTableProps = {
  files: CodeFileDto[];
  queryKey: QueryKey;
  projectId?: string | null;
  initialSortModel?: GridSortModel;
  columnVisibilityModel?: GridColumnVisibilityModel;
  loading?: boolean;
  onColumnVisibilityModelChange?: (nextModel: GridColumnVisibilityModel) => void;
};

const formatPercent = (value: number | null | undefined): string =>
  value === null || value === undefined ? '-' : `${value.toFixed(2)}%`;

const formatCountPair = (
  covered: number | null | undefined,
  total: number | null | undefined,
): string => {
  if (covered === null || covered === undefined || total === null || total === undefined) {
    return '-';
  }
  return `${String(covered)} / ${String(total)}`;
};

const buildMetricColumn = (
  field: string,
  headerName: string,
  thresholdKey: CoverageMetricThresholdKey,
  resolvePercent: (row: CodeFileDto) => number | null | undefined,
  resolveCovered: (row: CodeFileDto) => number | null | undefined,
  resolveTotal: (row: CodeFileDto) => number | null | undefined,
  resolveThreshold: (metricKey: CoverageMetricThresholdKey) => MetricThreshold,
): GridColDef<CodeFileDto> => ({
  field,
  headerName,
  flex: 0.55,
  sortable: false,
  filterable: false,
  align: 'left',
  headerAlign: 'left',
  cellClassName: 'table-metric-cell',
  renderCell: (params) => (
    <TableCellContainer gap="none">
      <CoverageMetricTableCell
        primary={formatPercent(resolvePercent(params.row))}
        secondary={formatCountPair(resolveCovered(params.row), resolveTotal(params.row))}
        primaryColor={resolveCoverageMetricThresholdColor(
          resolvePercent(params.row),
          resolveThreshold(thresholdKey),
        )}
      />
    </TableCellContainer>
  ),
});

const compareStrings = (
  leftValue: string | null | undefined,
  rightValue: string | null | undefined,
): number => (leftValue ?? '').localeCompare(rightValue ?? '', undefined, { sensitivity: 'base' });

const compareNumbers = (
  leftValue: number | null | undefined,
  rightValue: number | null | undefined,
): number => (leftValue ?? Number.NEGATIVE_INFINITY) - (rightValue ?? Number.NEGATIVE_INFINITY);

const compareDates = (
  leftValue: Date | string | null | undefined,
  rightValue: Date | string | null | undefined,
): number => new Date(leftValue ?? 0).getTime() - new Date(rightValue ?? 0).getTime();

const sortFiles = (files: CodeFileDto[], input: GridQueryInput): CodeFileDto[] => {
  const sort = input.sorts[0] ?? { field: 'normalizedPath', direction: 'asc' as const };
  const factor = sort.direction === 'asc' ? 1 : -1;

  return [...files].sort((left, right) => {
    let result = 0;
    switch (sort.field) {
      case 'fileName':
        result = compareStrings(left.fileName, right.fileName);
        break;
      case 'normalizedPath':
        result = compareStrings(left.normalizedPath, right.normalizedPath);
        break;
      case 'linesPct':
        result = compareNumbers(left.linesPct, right.linesPct);
        break;
      case 'functionsPct':
        result = compareNumbers(left.functionsPct, right.functionsPct);
        break;
      case 'branchesPct':
        result = compareNumbers(left.branchesPct, right.branchesPct);
        break;
      case 'statementsPct':
        result = compareNumbers(left.statementsPct, right.statementsPct);
        break;
      case 'capturedAt':
        result = compareDates(
          left.latestCapturedAt ?? left.createdAt,
          right.latestCapturedAt ?? right.createdAt,
        );
        break;
      default:
        result = compareStrings(left.fileName, right.fileName);
        break;
    }

    return result * factor;
  });
};

export const CoverageCodeFilesTable = ({
  files,
  queryKey,
  projectId = null,
  initialSortModel,
  columnVisibilityModel,
  loading = false,
  onColumnVisibilityModelChange,
}: CoverageCodeFilesTableProps): ReactElement => {
  const { thresholds } = useProjectMetricThresholds(projectId);
  const [editTarget, setEditTarget] = useState<CodeFileDto | null>(null);
  const resolveThreshold = useCallback(
    (metricKey: CoverageMetricThresholdKey) => thresholds.files[metricKey],
    [thresholds.files],
  );
  const {
    deleteLoading,
    deleteTarget,
    onDeleteClose,
    onDeleteConfirm,
    onDeleteRow,
    onUpdateRowTagsColor,
    updateRowTagsColorPendingId,
  } = useCodeFileTableRowActions({ selectedProjectId: projectId });
  const columns = useMemo<Array<GridColDef<CodeFileDto>>>(
    () => [
      {
        field: 'file',
        headerName: 'File',
        flex: 1,
        sortable: false,
        cellClassName: 'table-name-cell',
        renderCell: (params) => (
          <TableCellContainer gap="none">
            <TableNameCell
              primary={{
                label: params.row.fileName,
                href: `/coverage-files/${params.row.id}`,
                sx: { fontFamily: (theme) => theme.typography.mono.fontFamily },
              }}
              secondary={{
                label: params.row.normalizedPath,
                sx: { fontFamily: (theme) => theme.typography.mono.fontFamily },
              }}
            />
          </TableCellContainer>
        ),
      },
      buildMetricColumn(
        'lineCoverage',
        'Line',
        'line',
        (row) => row.linesPct,
        (row) => row.linesCovered,
        (row) => row.linesTotal,
        resolveThreshold,
      ),
      buildMetricColumn(
        'functionCoverage',
        'Function',
        'function',
        (row) => row.functionsPct,
        (row) => row.functionsCovered,
        (row) => row.functionsTotal,
        resolveThreshold,
      ),
      buildMetricColumn(
        'branchCoverage',
        'Branch',
        'branch',
        (row) => row.branchesPct,
        (row) => row.branchesCovered,
        (row) => row.branchesTotal,
        resolveThreshold,
      ),
      buildMetricColumn(
        'statementCoverage',
        'Statement',
        'statement',
        (row) => row.statementsPct,
        (row) => row.statementsCovered,
        (row) => row.statementsTotal,
        resolveThreshold,
      ),
      {
        field: 'details',
        headerName: getDetailLabel('details'),
        flex: 0.9,
        sortable: false,
        filterable: false,
        cellClassName: 'table-meta-details-cell table-meta-details-four-line-cell',
        renderCell: (params) => {
          const capturedAt = params.row.latestCapturedAt ?? params.row.createdAt;
          const capturedLabel = formatRelativeTime(capturedAt);
          const capturedTitle = formatDate(capturedAt, { includeTime: true });

          return (
            <TableCellContainer gap="none">
              <TableBreakdownStack>
                <TableBreakdownLine
                  rowTitle="Path"
                  icon={
                    <DescriptionOutlinedIcon sx={{ fontSize: (theme) => theme.app.iconSizes.sm }} />
                  }
                  content={params.row.normalizedPath}
                  disableLinks
                  contentSx={{ fontFamily: (theme) => theme.typography.mono.fontFamily }}
                />
                <TableBreakdownLine
                  rowTitle="Directory"
                  icon={<FolderOutlinedIcon sx={{ fontSize: (theme) => theme.app.iconSizes.sm }} />}
                  content={params.row.directoryPath ?? '-'}
                  disableLinks
                  contentSx={{ fontFamily: (theme) => theme.typography.mono.fontFamily }}
                />
                <TableBreakdownLine
                  rowTitle="Latest"
                  icon={<EventOutlinedIcon sx={{ fontSize: (theme) => theme.app.iconSizes.sm }} />}
                  content={capturedLabel.length > 0 ? capturedLabel : '-'}
                  valueTitle={capturedTitle}
                  disableLinks
                  contentSx={STATUS_META_TIMESTAMP_CONTENT_SX}
                  valueWrapperSx={STATUS_META_TIMESTAMP_VALUE_WRAPPER_SX}
                />
                <TableBreakdownLine
                  rowTitle="Tags"
                  icon={
                    <DescriptionOutlinedIcon sx={{ fontSize: (theme) => theme.app.iconSizes.sm }} />
                  }
                  content={formatEntityTagsLabel(params.row.tags)}
                  disableLinks
                />
              </TableBreakdownStack>
            </TableCellContainer>
          );
        },
      },
      {
        field: 'actions',
        headerName: '',
        flex: 0.1,
        filterable: false,
        sortable: false,
        disableColumnMenu: true,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => (
          <TableActionsCell
            rowName={params.row.fileName}
            editActionLabel={getEntityActionLabel('edit', 'Code File')}
            onEdit={() => {
              setEditTarget(params.row);
            }}
            disableEdit={updateRowTagsColorPendingId === params.row.id}
            showEditTagsAction={false}
            deleteActionLabel={getEntityActionLabel('delete', 'Code File')}
            onDelete={() => {
              onDeleteRow(params.row);
            }}
          />
        ),
      },
    ],
    [onDeleteRow, resolveThreshold, updateRowTagsColorPendingId],
  );

  const queryFn = useCallback(
    async (input: GridQueryInput): Promise<QueryResult<CodeFileDto>> => {
      const sortedFiles = sortFiles(files, input);
      const page = Math.max(input.page, 1);
      const pageSize = input.pageSize;
      const startIndex = (page - 1) * pageSize;
      return await Promise.resolve({
        items: sortedFiles.slice(startIndex, startIndex + pageSize),
        total: sortedFiles.length,
        page,
        pageSize,
      });
    },
    [files],
  );

  return (
    <>
      <ServerDataTable<CodeFileDto>
        queryPath="coverage-code-files/local-table"
        persistKey={null}
        syncStateToUrl={false}
        fixedFilters={[]}
        initialSortModel={initialSortModel}
        queryFn={queryFn}
        queryKey={queryKey}
        externalLoading={loading}
        columns={columns}
        getRowId={(row) => row.id}
        pageSizeOptions={[5, 10, 25, 50]}
        initialPageSize={5}
        noRowsText={getEmptyStateMessage('entity.noneFound', { entity: 'file' })}
        disableColumnSorting
        disableColumnMenu
        interactionMode="static"
        autoHeight
        columnVisibilityModel={columnVisibilityModel}
        getRowHeight={() => TABLE_ROW_HEIGHT}
        sx={[
          ...(Array.isArray(tableSx) ? tableSx : [tableSx]),
          {
            '& .MuiDataGrid-cell[data-field="file"], & .MuiDataGrid-cell[data-field="lineCoverage"], & .MuiDataGrid-cell[data-field="functionCoverage"], & .MuiDataGrid-cell[data-field="branchCoverage"], & .MuiDataGrid-cell[data-field="statementCoverage"], & .MuiDataGrid-cell[data-field="details"], & .MuiDataGrid-cell[data-field="actions"]':
              {
                alignItems: 'flex-start',
              },
            '& .MuiDataGrid-columnHeaders': {
              minHeight: `${TABLE_GRID_HEADER_HEIGHT}px !important`,
            },
          },
        ]}
        gridProps={{
          onColumnVisibilityModelChange,
        }}
      />
      <ConfirmDialog
        open={deleteTarget !== null}
        title={getDialogMessage('destructive.delete.title', { entity: 'codeFile' })}
        description="This code file entry will be removed from the coverage file list."
        confirmLabel={getDialogMessage('destructive.delete.confirmLabel', { entity: 'codeFile' })}
        confirmColor="error"
        loading={deleteLoading}
        onClose={onDeleteClose}
        onConfirm={onDeleteConfirm}
      />
      <DetailEditEntityDialog
        open={editTarget !== null}
        entityLabel="Code File"
        entityName={editTarget?.fileName ?? ''}
        projectId={editTarget?.projectId ?? null}
        tags={editTarget?.tags ?? []}
        loading={updateRowTagsColorPendingId === (editTarget?.id ?? null)}
        onClose={() => {
          if (updateRowTagsColorPendingId === (editTarget?.id ?? null)) return;
          setEditTarget(null);
        }}
        onSave={({ tags, colors }) => {
          if (!editTarget) return;
          onUpdateRowTagsColor({ row: editTarget, tags, colors });
          setEditTarget(null);
        }}
      />
    </>
  );
};
